/**
 * @file NewProjectDialog.js
 * @description Auto-maintained cleanup: comments & minor a11y/robustness. No UI/feature changes.
 */
import React, { useState, useEffect } from 'react';
import './NewProjectDialog.css';
import { fetchWithTrace } from '../services/fetchWithTrace';
import { useOnboarding } from './OnboardingContext';
import { useTutorialProjectDialog } from './useTutorialProjectDialog';

const NewProjectDialog = ({ onClose, onCreate }) => {
  const { isActive, currentStep, nextStep } = useOnboarding();
  const [title, setTitle] = useState('');
  const [projectType, setProjectType] = useState('story');
  const [story, setStory] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  // Auto-create countdown logic
  useEffect(() => {
    if (timeLeft === null) return;

    if (timeLeft === 0) {
      handleCreate();
      setTimeLeft(null);
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  const stopAutoCreate = () => {
    if (timeLeft !== null) {
      setTimeLeft(null); // Stop the timer permanently on interaction
    }
  };

  const { handleTutorialCreate } = useTutorialProjectDialog({
    isActive, currentStep, nextStep,
    setTitle, setProjectType, setStory, setIsGenerating,
    onCreate, onClose, title, story
  });

  const handleCreate = async () => {
    if (handleTutorialCreate()) return;
    if (projectType === 'idea') {
      try {
        setIsGenerating(true);
        setTimeLeft(null); // Stop any existing timer

        const { data } = await fetchWithTrace(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/text/generateStoryFromConcept`, {
          method: 'POST',
          body: {
            concept: story,
            title: title
          }
        });

        if (data && data.story) {
          // Review Flow: Populate story, switch tab, and start auto-create timer
          setStory(data.story);
          setProjectType('story');
          setTimeLeft(5);
        } else {
          alert("Failed to generate story from concept.");
        }
      } catch (error) {
        console.error("Error generating story:", error);
        alert("Error generating story. Please try again.");
      } finally {
        setIsGenerating(false);
      }
    } else {
      onCreate({ title, story });
      onClose();
    }
  };

  const handleExperimentClick = () => {
    if (projectType === 'idea') {
      setTitle("Jasper");
      setStory("A lonely maintenance robot on a deserted space station discovers an old guitar and tries to understand the concept of music, accidentally waking up a dormant AI that hates noise.");
    } else {
      setTitle("Max and the Treasure of Willow Park");
      setStory(`Max, the goldendoodle, bounded ahead through the forest trail, his curly coat glinting in the sunlight. Emma laughed, trying to keep up, while Adam, her boyfriend, followed behind with a backpack full of snacks and water.

“Your dog’s got more energy than both of us combined,” Adam teased, adjusting his pack.

“He’s not just my dog,” Emma said proudly. “He’s family.”

Max stopped suddenly near the base of an old oak tree, nose buried in the dirt. He barked once, then began digging furiously. Emma and Adam exchanged a curious glance and knelt beside him. Together, they brushed away leaves and soil until Adam’s hand hit something hard.

It was a small wooden box, weathered by time. Inside lay a faded letter and a single golden coin. Emma carefully read the letter aloud:

“To whoever finds this: May your adventures always be golden.”

Adam raised an eyebrow. “Guess Max just found us buried treasure.”

But before Emma could respond, Max snatched the coin in his mouth and bolted deeper into the woods, tail wagging wildly. Emma chased after him, laughing breathlessly, while Adam shook his head. “So much for retirement money!” he called, sprinting after them.

They finally caught up with Max at the riverbank. He dropped the coin at Emma’s feet and barked proudly, as if to say, See? I told you it was important. Emma hugged him tightly, her eyes sparkling. Adam, catching his breath, put his arm around both of them.

“Looks like the real treasure,” he said softly, “is this little team we’ve got.”

As the sun dipped low, painting the sky gold, Max nestled between Emma and Adam, his tail wagging in contentment. The coin might have been old and forgotten, but their adventure together felt priceless.`);
    }
    stopAutoCreate();
  };


  const isValid = title.trim().length > 0 && story.trim().length > 0;

  return (
    <div className="new-project-dialog-overlay">
      <div className="new-project-dialog">
        <div className="title-bar">
          <h3>Create Movie</h3>
        </div>

        <label>Title:</label>
        <div className="input-row">
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter title..." />

          <select
            className="project-type-select"
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
          >
            <option value="story">STORY</option>
            <option value="idea">CONCEPT</option>
          </select>

          <button className="experiment-icon-btn" title="Experiment with a sample" onClick={handleExperimentClick}>
            <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <path d="M14.5 2H9.5C8.67157 2 8 2.67157 8 3.5V8.5L4.2 16.1C3.5 17.5 4.5 19.5 6 19.5H18C19.5 19.5 20.5 17.5 19.8 16.1L16 8.5V3.5C16 2.67157 15.3284 2 14.5 2ZM11 16C12.1046 16 13 15.1046 13 14C13 12.8954 12.1046 12 11 12C9.89543 12 9 12.8954 9 14C9 15.1046 9.89543 16 11 16ZM15 13C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11C14.4477 11 14 11.4477 14 12C14 12.5523 14.4477 13 15 13Z" />
              <circle cx="15" cy="6" r="1.5" fill="currentColor" />
              <circle cx="10" cy="5" r="1" fill="currentColor" />
            </svg>
          </button>
        </div>

        <label>{projectType === 'story' ? 'Story:' : 'Concept | Idea:'}</label>
        <textarea
          id="new-project-idea-input"
          value={story}
          onChange={(e) => { setStory(e.target.value); stopAutoCreate(); }}
          onScroll={stopAutoCreate}
          onClick={stopAutoCreate}
          onKeyDown={stopAutoCreate}
          disabled={isGenerating}
        ></textarea>

        <div className="dialog-buttons">
          <button id="btn-submit-new-project" onClick={handleCreate} disabled={!isValid || isGenerating}>
            {isGenerating ? 'Generating...' : (projectType === 'idea' ? 'Generate Story' : (timeLeft ? `Create Movie (${timeLeft})` : 'Create Movie'))}
          </button>
          <button onClick={onClose} disabled={isGenerating}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default NewProjectDialog;
